import {
  IFilterFlag,
  IListingState,
} from "contexts/ListingContextHelpers/interfaces";
import {
  __filterSelectedGroup,
  __filterSelectedOptions,
  getUrlPage,
  processSelectedSortingFields,
} from "contexts/ListingContextHelpers/reducerHelpers";
import { INeighborhoodLiteAsset } from "models/Assets/INeighborhoodAsset";
import {
  IDynamicAttributeEnhanced,
  IDynamicAttributeSortField,
} from "models/Assets/IDynamicAttributes";
import { getDefaultOrderModes } from "helpers/osHelpers";
import { getLang } from "helpers/themeHelpers";
import { __getAjaxClient } from "utils/misc/fetchClient";
import {
  requestPostsApi,
  requestRecommendedAds,
  requestSeo,
} from "contexts/ListingContextHelpers/reducersRequests";
import {
  addQueryParamsToLinkPostView,
  EnumPostRef,
  linkToPostView,
} from "helpers/links-builders/post";
import { EnumSort } from "../../enums/EnumSort";

export const PREV_URL = "prevUrl";
const updateQueryString = (prevState: IListingState): IListingState => {
  const dynamicAttributes = {};
  prevState.selectedCpsOptions.forEach((option) => {
    dynamicAttributes[option.fieldName] =
      dynamicAttributes[option.fieldName] || [];
    dynamicAttributes[option.fieldName].push(option.id);
  });
  Object.keys(prevState.dynamicRangeAttributes).forEach((fieldName) => {
    dynamicAttributes[fieldName] = {
      from: prevState.dynamicRangeAttributes[fieldName].from || null,
      to: prevState.dynamicRangeAttributes[fieldName].to || null,
      unit: prevState.dynamicRangeAttributes?.[fieldName]?.unit?.id || null,
    };
  });
  prevState.queryString = {
    ...prevState.queryString,
    allCPsMap: prevState.allCPsMap,
    page: prevState.pageNumber,
    PostSearch: {
      ...prevState.queryString.PostSearch,

      priceFrom: prevState.queryString.PostSearch.priceFrom,
      priceTo: prevState.queryString.PostSearch.priceTo,
      price_currency: prevState?.currency?.id,

      categoryId: prevState?.category?.id
        ? Number(prevState?.category?.id)
        : null,
      subCategoryId: prevState?.subCategory?.id
        ? Number(prevState?.subCategory?.id)
        : null,
      cityId: prevState.city ? String(prevState.city.id) : null,
      neighborhood_id: prevState.neighborhoods
        ? prevState.neighborhoods.map((n) => n.id).join(",")
        : null,
      sort: prevState.selectedSortingField?.solrField as EnumSort,
      orderMode: prevState.selectedSortingField?.orderMode,
      ...(Object.keys(dynamicAttributes).length > 0
        ? { dynamicAttributes }
        : { dynamicAttributes: null }),
    },
  };
  prevState.queryString.filterFlags =
    (prevState.selectedFilterFlags || []).join(",") || null;

  return prevState;
};

const clearFilters = (prevState: IListingState): IListingState => {
  prevState.listingScrollYPosition = null;
  prevState.selectedFilterFlags = [];
  prevState.category = prevState.subCategory = null;
  prevState.allCPsMap = {};
  prevState.selectedCpsOptions = [];
  prevState.dynamicRangeAttributes = {};
  prevState.queryString.PostSearch.priceTo = null;
  prevState.queryString.PostSearch.priceFrom = null;
  prevState.assets = {
    ...prevState.assets,
    subCategories: [],
    dynamicAttributeEnhancedAssets: [],
    allSortCasesForSelectedCategory: getDefaultOrderModes(),
  };
  prevState.selectedSortingField = processSelectedSortingFields(
    prevState.assets.allSortCasesForSelectedCategory,
    { PostSearch: {} }
  );
  prevState.queryString = {
    ...prevState.queryString,
    dlpSlug: null,
  };

  return prevState;
};
const requestDynamicAttributesEnhanced = async (
  prevState: IListingState
): Promise<IListingState> => {
  const locale = getLang();
  const { subCategory } = prevState;
  if (subCategory) {
    const {
      dynamicAttributeEnhancedAssets,
      allSortCasesForSelectedCategory,
      filterFlags,
    } = await __getAjaxClient<{
      dynamicAttributeEnhancedAssets: IDynamicAttributeEnhanced[];
      allSortCasesForSelectedCategory: IDynamicAttributeSortField[];
      filterFlags: IFilterFlag[];
    }>(
      `/requestDynamicAttributesEnhanced?subCategoryId=${subCategory.id}&locale=${locale}`
    );
    prevState.assets = {
      ...prevState.assets,
      dynamicAttributeEnhancedAssets,
      allSortCasesForSelectedCategory,
      filterFlags,
    };
  }
  return prevState;
};

const maintainNHoods = async (
  prevState: IListingState
): Promise<IListingState> => {
  const locale = getLang();
  const cityId = prevState.city?.id;
  let nHoods = [];
  if (cityId) {
    nHoods = await __getAjaxClient<INeighborhoodLiteAsset[]>(
      `/requestNHoods?cityId=${cityId}&locale=${locale}`
    );
  }
  prevState.assets = {
    ...prevState.assets,
    nHoods,
  };
  return prevState;
};

const reduceNhoods = (prevState: IListingState, nHood: any): IListingState => {
  if (Array.isArray(nHood)) {
    // in case of mobile popup select multi
    prevState.neighborhoods = nHood;
  } else if (prevState.neighborhoods) {
    const found =
      prevState.neighborhoods.findIndex((n) => n.id === nHood.id) > -1;
    if (found) {
      prevState.neighborhoods = prevState.neighborhoods.filter(
        (n) => n.id !== nHood.id
      );
    } else {
      prevState.neighborhoods.push(nHood as INeighborhoodLiteAsset);
    }
  }
  return prevState;
};

const onToggleDynamicAttribute = (
  prevState: IListingState,
  attr,
  toggledOption,
  forcedSelectedOptions
): IListingState => {
  if (toggledOption) {
    const [filteredSelectedOptions, newAllCPsMap] = __filterSelectedOptions(
      prevState.allCPsMap,
      prevState.selectedCpsOptions,
      toggledOption,
      prevState.assets
    );
    prevState.allCPsMap = newAllCPsMap;
    prevState.selectedCpsOptions = filteredSelectedOptions;
  } else if (forcedSelectedOptions) {
    prevState.selectedCpsOptions = [
      ...prevState.selectedCpsOptions.filter(
        (o) => o.fieldName !== attr.fieldName
      ),
      ...forcedSelectedOptions,
    ];
  }
  return prevState;
};
const onToggleGroup = (
  prevState: IListingState,
  attr,
  toggledOption,
  forcedSelectedOptions
): IListingState => {
  if (toggledOption) {
    const [filteredSelectedOptions, newAllCPsMap] = __filterSelectedGroup(
      prevState.allCPsMap,
      prevState.selectedCpsOptions,
      toggledOption,
      prevState.assets
    );
    prevState.allCPsMap = newAllCPsMap;
    prevState.selectedCpsOptions = filteredSelectedOptions;
  } else if (forcedSelectedOptions) {
    prevState.selectedCpsOptions = [
      ...prevState.selectedCpsOptions.filter(
        (o) => o.fieldName !== attr.fieldName
      ),
      ...forcedSelectedOptions,
    ];
  }
  return prevState;
};

const onToggleCpAll = (prevState: IListingState, attr): IListingState => {
  prevState.allCPsMap[attr.fieldName] = !prevState.allCPsMap[attr.fieldName];
  if (prevState.allCPsMap[attr.fieldName]) {
    prevState.selectedCpsOptions = prevState.selectedCpsOptions.filter(
      (o) => o.fieldName !== attr.fieldName
    );
  }
  Object.keys(prevState.allCPsMap).forEach((k) => {
    if (!prevState.allCPsMap[k]) {
      delete prevState.allCPsMap[k];
    }
  });
  prevState.allCPsMap = { ...prevState.allCPsMap };
  return prevState;
};

const maintainChildFieldsSelection = async (
  prevState: IListingState
): Promise<IListingState> => {
  const locale = getLang();
  if (prevState.assets?.dynamicAttributeEnhancedAssets) {
    const [parentAttr] = prevState.assets.dynamicAttributeEnhancedAssets.filter(
      (attr) => attr.field.hasChild
    );
    if (!parentAttr) return prevState;
    const childFieldName = `${parentAttr.fieldName}_child`;
    const childFieldIndex =
      prevState.assets.dynamicAttributeEnhancedAssets.findIndex(
        (attr) => attr.fieldName === childFieldName
      );
    const parentOptionIds = prevState.selectedCpsOptions
      .filter((c) => c.fieldName === parentAttr.fieldName)
      .map((c) => Number(c.id));

    if (prevState.subCategory && parentAttr && childFieldIndex > -1) {
      const dynamicAttributeEnhancedAssets =
        prevState.assets.dynamicAttributeEnhancedAssets;
      const childField = await __getAjaxClient<IDynamicAttributeEnhanced>(
        `/requestCPChildField?subCategoryId=${
          prevState.subCategory.id
        }&fieldName=${
          parentAttr.fieldName
        }&parentOptionIds=${parentOptionIds.join(",")}&locale=${locale}`
      );
      if (childField) {
        dynamicAttributeEnhancedAssets[childFieldIndex] =
          childField as IDynamicAttributeEnhanced;
      } else {
        dynamicAttributeEnhancedAssets[childFieldIndex].options = [];
      }
      prevState.assets.dynamicAttributeEnhancedAssets = [
        ...dynamicAttributeEnhancedAssets,
      ];
    }
  }

  const brandParentAttrCount = prevState.selectedCpsOptions.filter(
    (item) =>
      (item.fieldName.includes("Brand") || item.fieldName.includes("Type_")) &&
      !item.fieldName.includes("_child")
  ).length;

  if (brandParentAttrCount >= 2) {
    prevState.selectedCpsOptions = prevState.selectedCpsOptions.filter(
      (op) => !op.fieldName.includes("_child")
    );
  }
  return prevState;
};

export const navigationReducer = async (
  prevState: IListingState,
  actionType,
  payload: any
) => {

  switch (actionType) {
    case "LISTING_PUSH_OR_POP":
      prevState = {
        ...payload.historyState,
        listingScrollYPosition: null,
        postsApiLoading: false,
      };
      prevState = await requestPostsApi(prevState)
        .then((theState) => {
          prevState = { ...theState };
          return requestRecommendedAds(theState).then((theState2) => {
            prevState = { ...theState2 };
            return requestSeo(prevState).then((theState3) => {
              return { ...theState3 };
            });
          });
        })
        .catch(() => prevState);
      break;
    case "RESET_SCROLL_Y_POSITION":
      prevState = { ...prevState, listingScrollYPosition: null };
      break;
    case "BACK_FROM_QUICK_POST_VIEW":
      prevState = { ...payload.historyState };
      break;
    case "QUICK_POST_VIEW":
        window[PREV_URL] =  getUrlPage(prevState)
      history.replaceState(
        { ...prevState, listingScrollYPosition: window.scrollY + 1 },
        "",
        getUrlPage(prevState)
      );
      prevState = {
        ...prevState,
        view: "postView",
        selectedPost: {
          ...payload.post,
          isQuickPost: true,
          responseTime: "",
          member: {},
          postTags: [],
          services: [],
          phone: "",
          nextPrev: {
            prev_url: "",
            next_url: "",
          },
        },
      };
      const postViewLink = payload.isRecommended ? addQueryParamsToLinkPostView(linkToPostView,prevState.selectedPost, EnumPostRef.SERP , null , EnumPostRef.RECOMMENDED):linkToPostView(prevState.selectedPost);
      window.history.pushState(
        prevState,
        "",
          postViewLink
      );
      window.scroll(0, 0);
      break;
  }
  return prevState;
};
export const asyncReducer = async (
  prevState: IListingState,
  actionType,
  payload: any
) => {
  prevState = { ...prevState, pageNumber: 1 };
  const { toggledOption, forcedSelectedOptions } = payload || {};

  if (actionType !== "SET_PAGE") {
    delete  prevState.queryString.similarSerp;
  }

  switch (actionType) {
    case "INITIAL_REQUEST":
      // keep this empty plz
      break;
    case "CLEAR_FILTERS":
      prevState = { ...clearFilters(prevState), city: null, neighborhoods: [] };
      break;
    case "UPDATE_MAIN_CATEGORY":
      const { category } = payload;
      if (category?.id !== prevState?.category?.id) {
        prevState = clearFilters(prevState);
        prevState.category = category;
        prevState.assets = {
          ...prevState.assets,
          dynamicAttributeEnhancedAssets: [],
          allSortCasesForSelectedCategory: getDefaultOrderModes(),
        };
      }
      break;
    case "UPDATE_SUB_CATEGORY":
      const { subCategory } = payload;
      const subCatConsidered =
        prevState.category && subCategory ? subCategory : null;

      const mainCat = { ...prevState.category };
      const subCategories = [...(prevState.assets?.subCategories || [])];
      prevState = clearFilters(prevState);
      prevState.assets = {
        ...prevState.assets,
        subCategories,
      };

      prevState.category = mainCat;
      prevState.subCategory = subCatConsidered;
      prevState.selectedFilterFlags = [];
      if (subCatConsidered) {
        prevState = await requestDynamicAttributesEnhanced(prevState);
      }
      break;
    case "TOGGLE_CITY":
      const { city } = payload;
      prevState.city = city?.id === prevState?.city?.id ? null : city;
      prevState.neighborhoods = [];
      prevState = await maintainNHoods(prevState);
      break;
    case "TOGGLE_NHOOD":
      const { nHood } = payload;
      prevState = reduceNhoods(prevState, nHood);
      break;
    case "TOGGLE_CP_OPTION":
      prevState = onToggleDynamicAttribute(
        prevState,
        payload.attr,
        toggledOption,
        forcedSelectedOptions
      );
      prevState = await maintainChildFieldsSelection(prevState);
      break;
      case "TOGGLE_GROUP":
      prevState = onToggleGroup(
        prevState,
        payload.attr,
        toggledOption,
        forcedSelectedOptions
      );
      prevState = await maintainChildFieldsSelection(prevState);
      break;
    case "UPDATE_CURRENCY":
      prevState.currency = payload.currency;
      break;
    case "TOGGLE_CP_ALL":
      prevState = onToggleCpAll(prevState, payload.attr);
      break;
    case "DEL_RANGE_ATTR_FIELD":
      delete prevState.dynamicRangeAttributes[payload.fieldName];
      if (payload.fieldName === "Car_Year") {
        prevState.selectedCpsOptions = prevState.selectedCpsOptions.filter(
          (op) => op.fieldName !== "Car_Year"
        );
      }

      prevState.dynamicRangeAttributes = {
        ...prevState.dynamicRangeAttributes,
      };
      break;
    case "SET_RANGE_ATTR_FIELD":
      prevState.dynamicRangeAttributes = {
        ...prevState.dynamicRangeAttributes,
        [payload.fieldName]: {
          ...payload.valueOrUnit, //{from: xxx, to: xxx: unit?:11}
        },
      };
      break;
    case "UPDATE_SORTING":
      const { sortLabel } = payload;
      const [selectedSortingField] =
        prevState.assets.allSortCasesForSelectedCategory.filter(
          (it) => it.label === sortLabel
        );
      prevState.selectedSortingField = selectedSortingField;
      break;
    case "SET_PRICE_RANGE_WITH_CURRENCY":
      const { priceFrom, priceTo } = payload;
      prevState.currency = payload.currency;
      prevState.queryString.PostSearch.priceTo = priceTo;
      prevState.queryString.PostSearch.priceFrom = priceFrom;
      break;
    case "SET_PAGE":
      const { pageNumber } = payload;
      prevState.pageNumber = pageNumber;
      break;
    case "SET_QUERYSTRING":
      prevState.queryString = {
        ...prevState.queryString,
        PostSearch: {
          ...prevState.queryString.PostSearch,
          [payload.fieldName]: payload.valueAny,
        },
      };
      break;
    case "SET_FLAGS":
      const { filterFlagKey, isChecked } = payload;

      if (isChecked) {
        //push to array
        prevState.selectedFilterFlags = [
          ...(prevState.selectedFilterFlags || []),
          filterFlagKey,
        ];
      } else {
        //we should remove the flag
        prevState.selectedFilterFlags = (
          prevState.selectedFilterFlags || []
        ).filter((it) => it !== filterFlagKey);
      }
      break;
  }
  prevState = updateQueryString(prevState);

  return prevState;
};
